import React from 'react';
import YouTube from 'react-youtube';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSearch, faDownload} from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';

import { faComment } from '@fortawesome/free-regular-svg-icons';
import Authenticated from 'Components/Layouts/Authenticated';
import CmsElement from 'Components/Utilities/CmsElement';
import {Loading} from 'Components/Partials';
import {ModalTrigger} from 'Components/Modal';
import ViewCmsElementRecordModal from 'Components/Partials/Modals/ViewCmsElementRecordModal';

import User from 'Services/User';
import {translation} from 'Services/TranslationHelpers';
import {getRecordValue} from 'Services/CmsHelpers';
import {roundedElements} from 'Services/BaseHelpers';

import OnDemandItem from './OnDemandItem';
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import View from "Services/Api/Cms/Elements/View";
// DomLink not used in this component
import JoinUs from 'Components/Partials/JoinUs';

export default class OnDemand extends CmsElement {
    /**
     * @constructor
     */
    constructor() {
        super();
        this.topVideoRef = React.createRef();
        // store the youtube player instance for the top player
        this.topPlayer = null;
        // refs for horizontal sections (header groups)
        this.sectionRefs = {};
    }

    /**
     * @var cmsElementIdentifier
     * @type {string}
     */
    cmsElementIdentifier = 'on-demand-legacy';

    /**
     * @var paginate
     * @type {boolean}
     */
    paginate = false;

    /**
     * @var useHeaders
     * @type {boolean}
     */
    useHeaders = true;

    /**
     * @var useTopVideo
     * @type {boolean}
     */
    useTopVideo = true;

    /**
     * @var state
     */
    state = {
        working: true,
        topVideo: null,
    };

    /**
     * @method setTopVideo
     * @param {object} item
     */
    setTopVideo = (item) => {
        if ( !getRecordValue ( item, 'video_src' ) ) {
            if ( getRecordValue ( item, 'download_file', 'file' ) ) {
                alert ( translation ( 'cms', 'click_download_icon' ) );
            }

            return;
        }

        if (User?.loggedIn) {
            this.handleView(item);
        }

        // set top video, then scroll it into view and attempt to autoplay
        this.setState({
            topVideo: item
        }, () => {
            this.scrollToTop();
            this.attemptAutoplay();
        });
    }

    /**
     * Scroll top video container into view
     */
    scrollToTop = () => {
        if (this.topVideoRef?.current) {
            try {
                this.topVideoRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (e) { /* ignore */ }
        }
    }

    /**
     * Try to autoplay the currently selected top video.
     * - YouTube: call API playVideo()
     * - Vimeo: add autoplay=1 to iframe src
     * - HTML5: call video.play()
     */
    attemptAutoplay = () => {
        const { topVideo } = this.state;
        if (!topVideo) return;

        const vType = getRecordValue(topVideo, 'video_type');

        if (vType === 'youtube') {
            if (this.topPlayer && typeof this.topPlayer.playVideo === 'function') {
                try { this.topPlayer.playVideo(); } catch (e) { /* ignore */ }
            }
            return;
        }

        // Vimeo / Livid: patch iframe src to enable autoplay
        if (vType === 'vimeo' || vType === 'livid') {
            const iframe = this.topVideoRef.current?.querySelector('iframe');
            if (iframe) {
                let src = iframe.src || getRecordValue(topVideo, 'video_src') || '';
                if (!/autoplay=1/.test(src)) {
                    src += (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';
                    iframe.src = src;
                }
            }
            return;
        }

        // native HTML5 video
        const video = this.topVideoRef.current?.querySelector('video');
        if (video) {
            try { video.play().catch(()=>{}); } catch (e) { /* ignore */ }
        }
    }

    setVideo = () => {
        const { records } = this.state;

        // Find the first item with a video source
        if (this.useTopVideo && records && records.data) {
            let topVideo = records.data.find(item => getRecordValue(item, 'video_src'));

            if (topVideo) {
                this.setTopVideo(topVideo);
            }
        }
    }

    /**
     * Return a numeric year for a group of items if possible.
     * Looks for common fields like `year`, `session_year` or any 4-digit 20xx in text fields.
     */
    getGroupYear = (items) => {
        if (!items || items.length === 0) return null;
        const first = items[0];

        const tryKeys = ['year', 'session_year', 'video_year'];
        for (let k of tryKeys) {
            const v = getRecordValue(first, k);
            if (v && /20\d{2}/.test(String(v))) return String(v).match(/20\d{2}/)[0];
        }

        // search for a year in title/header/description fields
        const fieldsToCheck = [getRecordValue(first, 'title'), getRecordValue(first, 'header'), getRecordValue(first, 'description')];
        for (let f of fieldsToCheck) {
            if (!f) continue;
            const m = String(f).match(/20\d{2}/);
            if (m) return m[0];
        }

        return null;
    }

    // horizontal scrolling controls removed — sections now render as multi-row grids

    /**
     * @method handleView
     * @param {object} item
     * @return {Promise<*>}
     */
    handleView = async (item) => {
        await View.post('', {
            //item_id: item.id,
            cms_element_record_id: item.id
        }, 'view');
    }

    componentDidUpdate( prevProps, prevState, snapshot ) {
        if ( this.state.records !== prevState.records ) {
            this.setVideo();
        }
    }

    /**
     * @method render
     * @return {*}
     */
    render () {
        const {working, records} = this.state;
        let headers = [];

        if (this.useHeaders && records && records.data) {
            headers = [...new Set(
                records.data.map(item => getRecordValue(item, 'header'))
            )];
        }

        // Top-level hero/intro (matches screenshot layout)
        return (
            <Authenticated pageVideoHeader={ "bupa-banner.mp4" }>
                <div>
                    {/* Hero section */}
                    <div className="w-full text-center text-white py-20" style={{backgroundColor: '#053a86'}}>
                        <div className="max-w-4xl mx-auto px-6">
                            <h2 className="text-3xl font-bold mb-6">Legacy of Symposium</h2>
                            <p className="leading-relaxed">
                                Bupa Symposium was created with a clear ambition: to bring together the world’s leading clinical minds, innovators, and healthcare sector leaders to shape the future of health.
                            </p>

                            <p className="leading-relaxed mt-6">
                                Bupa Symposium is now a defining moment in the healthcare calendar. A moment for public and private sector leaders to unite with the shared purpose of helping people live longer, healthier, happier lives, and making a better world.
                            </p>

                            <p className="leading-relaxed mt-6">
                                At its core, this flagship event reflects the belief that no single organisation can solve healthcare’s biggest challenges alone. Progress happens when sectors collaborate, when evidence meets lived experience, and when ideas move decisively from insight into action.
                            </p>
                        </div>
                    </div>

                    <div className="text-center w-full mx-auto">
                        <div className="w-full mx-auto">

                            {/*<p className={"text-center text-app-blue text-lg my-10"}>*/}
                            {/*    Click on the thumbnails below to re-watch all of the presentations from the Bupa*/}
                            {/*    Symposium 2025.  You can also access the audio recordings from the Focus Stage by*/}
                            {/*    clicking the download link.*/}
                            {/*</p>*/}

                            {/*<div className="relative w-full md:w-3/4 lg:w-1/2 mx-auto my-10">*/}
                            {/*    /!*{this.renderSearchBar()}*!/*/}
                            {/*    {this.useTopVideo && this.renderTopBox()}*/}
                            {/*</div>*/}

                            {working && <Loading />}

                            {!working &&
                                <div className="relative z-10">
                                    {records.data !== null && (!records.data || records.data.length === 0) && (
                                        <p className="text-center text-app-blue text-lg my-10">
                                            {translation('misc', 'no_items', {
                                                item: translation('cms', 'on_demand')
                                            })}
                                        </p>
                                    )}

                                    {records.data && this.useHeaders && headers.map((header, k) => {
                                        const items = records.data.filter(o => getRecordValue(o, 'header') === header);
                                        // year grouping not currently displayed
                                        const headerKey = `header-${k}`;

                                        // create a ref for the horizontal container if not present
                                        if (!this.sectionRefs[headerKey]) this.sectionRefs[headerKey] = React.createRef();

                                        return (
                                            <div key={k} className="py-12" style={{backgroundColor: '#DDEDF8'}}>
                                                <div className="max-w-7xl mx-auto px-6">
                                                    {/*{year && (*/}
                                                    {/*    <h3 className="text-center text-2xl font-bold text-app-blue mb-6">*/}
                                                    {/*        {`Highlights from ${year}`}*/}
                                                    {/*    </h3>*/}
                                                    {/*)}*/}

                                                    <div className="flex items-center mb-4">
                                                        <div className="text-sm font-semibold text-app-blue mr-auto">{header}</div>
                                                    </div>

                                                    {items.length === 0 && (
                                                        <p className="mb-3 text-center md:text-left text-white">
                                                            {translation('misc', 'no_items', {
                                                                item: translation('cms', 'on_demand')
                                                            })}
                                                        </p>
                                                    )}

                                                    {items.length > 0 && (
                                                        <div className="relative">
                                                            <div
                                                                ref={this.sectionRefs[headerKey]}
                                                                className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-2'}
                                                            >
                                                                {items.map((it, idx) => (
                                                                    <div key={idx} className="w-full">
                                                                        {this.renderItem(it, idx)}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {records.data && !this.useHeaders &&
                                        // Non-header mode: render as a horizontal list
                                        <div className={'relative'}>
                                            <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-2'}>
                                                {records.data.map((it, idx) => (
                                                    <div key={idx} className="w-full">
                                                        {this.renderItem(it, idx)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
                {/* Join Us footer */}
                <JoinUs />
            </Authenticated>
        );
    }

    /**
     * @method renderSearchBar
     * @return {JSX.Element}
     */
    renderSearchBar = () => {
        const {filters} = this.state

        let search = filters?.search ?? null;

        return (
            <div className="mx-auto flex justify-center relative z-10 mb-16" ref={this.topVideoRef}>
                <input
                    type="text"
                    className={`w-11/12 mt-6 rounded-full text-white
                        outline-none focus:outline-none focus:border-0 focus:ring-0 font-bold py-3 placeholder-white pl-10`}
                    placeholder={translation('misc', 'search')}
                    onChange={(e) => this.setFilter('on_demand_search', e.target.value)}
                    value={search}
                />

                <FontAwesomeIcon
                    icon={faSearch}
                    size="1x"
                    className="h-6 w-6 absolute top-9 right-16 text-white"
                />
            </div>
        );
    }

    /**
     * @method renderTopBox
     * @return {JSX.Element}
     */
    renderTopBox = () => {
        let {topVideo} = this.state;

        return (
            <div className="mb-24 relative z-10" ref={this.topVideoRef}>
                {/* Prominent title when a top video is selected */}
                {topVideo && getRecordValue(topVideo, 'title') &&
                    <div className="text-app-blue font-bold text-2xl mb-4 text-center md:text-left">
                        {getRecordValue(topVideo, 'title')}
                    </div>
                }
                <div className={`
                    mx-auto bg-app-grey flex flex-col justify-between
                    ${roundedElements() ? 'rounded-sm ' : ''}
                `}>
                    {!topVideo &&
                        <div className="h-80 flex justify-center">
                            <FontAwesomeIcon
                                icon={faPlay}
                                size="2x"
                                className="h-16 w-16 self-center text-app-blue"
                            />
                        </div>
                    }

                    {topVideo &&
                        this.renderItemBody(topVideo, 'index')
                    }

                    {this.renderItemBar(topVideo)}
                </div>
            </div>
        );
    }

    /**
     * @method renderItem
     * @param {object} item
     * @param {string} key
     * @return {JSX.Element}
     */
    renderItem = (item, key) => {
        // Wrap the item in a ModalTrigger so clicking the card opens the modal (dashboard-like behaviour)
        const item_title = getRecordValue(item, 'title');

        return (
            <ModalTrigger component={ViewCmsElementRecordModal} props={{
                item,
                handleToggleLike: this.handleToggleLike,
                handleView: this.handleView,
                handleCommentNumberUpdate: this.handleCommentNumberUpdate,
                title: item_title,
                body: this.renderItemBody,
                content: null
            }} key={key}>
                <OnDemandItem
                    item={item}
                    useTopVideo={this.useTopVideo}
                    setTopVideo={this.setTopVideo}
                    renderItemBar={this.renderItemBar}
                    renderItemBody={this.renderItemBody}
                />
            </ModalTrigger>
        );
    };

    /**
     * @method renderItemBar
     * @param {object} item
     * @return {JSX.Element}
     */
    renderItemBar = (item) => {
        let item_title = item ? getRecordValue(item, 'title') : null;
        let item_download_file = item ? getRecordValue(item, 'download_file', 'file') : null;

        return (
            <div className="flex justify-between p-2 bg-app-gray text-app-blue min-h-12 h-full">
                <div className="ml-2 flex">
                    <p className={"self-center"}>
                        {/*{item ? item_title : translation('cms', 'click_video_to_begin')}*/}
                    </p>
                </div>

                {item &&
                    <div className="flex items-center select-none">
                        {item_download_file &&
                            <a href={ item_download_file }
                               download
                               target="_blank"
                               className="mr-2"
                               onClick={ () => this.handleView ( item ) }
                            >
                                <FontAwesomeIcon
                                    icon={faDownload}
                                    size="1x"
                                    className="mx-1 cursor-pointer text-app-blue"
                                />
                            </a>
                        }
                        {User.loggedIn && (
                            <>
                                {item.allow_likes && (
                                    <div className="flex mr-2">
                                    <FontAwesomeIcon
                                        icon={item.user_has_liked ? faHeartSolid : faHeartRegular}
                                        size="1x"
                                        className={`mx-1 cursor-pointer text-app-purple transition duration-200 transform hover:scale-110`}
                                        onClick={() => this.handleToggleLike(item)}
                                    />

                                        {(item.total_likes !== undefined && item.total_likes > 0) &&
                                            <div className={"text-xs text-app-light-blue"}>
                                                {item.total_likes}
                                            </div>
                                        }
                                    </div>
                                )}

                                {item.allow_comments && (
                                    <ModalTrigger component={ViewCmsElementRecordModal} props={{
                                        item,
                                        handleToggleLike: this.handleToggleLike,
                                        handleView: this.handleView,
                                        handleCommentNumberUpdate: this.handleCommentNumberUpdate,
                                        title: item_title,
                                        body: this.renderItemBody,
                                        content: null,
                                    }}>
                                        <div className="flex">
                                            <FontAwesomeIcon
                                                icon={faComment}
                                                size="1x"
                                                className="mx-1 text-app-blue cursor-pointer transition duration-200 transform hover:scale-110"
                                            />

                                            {item.total_comments !== undefined && item.total_comments > 0 &&
                                                <div className={"text-xs text-app-blue"}>
                                                    {item.total_comments}
                                                </div>
                                            }
                                        </div>
                                    </ModalTrigger>
                                )}
                            </>
                        )}
                    </div>
                }
            </div>
        );
    }

    /**
     * @method renderItemBody
     * @param {object} item
     * @param {string} source
     * @return {JSX.Element}
     */
    renderItemBody = (item, source) => {
        // Iframe responsive; https://www.w3schools.com/howto/howto_css_responsive_iframes.asp

        let item_video_type = getRecordValue(item, 'video_type');
        let item_video_src = getRecordValue(item, 'video_src');

        return (
            <div className="h-full w-full">
                {item_video_type === 'youtube' && (
                    <YouTube
                        videoId={item_video_src}
                        id="2g811Eo7K8U"
                        className="w-full h-full"
                        containerClassName="w-full h-80"
                        onPlay={() => this.handleView(item)}
                        // capture the player instance when this is the top player
                        onReady={(e) => {
                            if (source === 'index') {
                                this.topPlayer = e.target;
                            }
                            // attempt autoplay when in modal
                            if (source === 'modal') {
                                try { e.target.playVideo && e.target.playVideo(); } catch(e){}
                            }
                        }}
                        opts={{ playerVars: { autoplay: source === 'modal' ? 1 : 0, rel: 0 } }}
                    />
                )}
                {item_video_type === 'vimeo' && (
                    <div className="relative h-full w-full" style={{paddingTop: '56.25%'}}>
                        <iframe src={ source === 'modal' && item_video_src && !/autoplay=1/.test(item_video_src) ? (item_video_src + (item_video_src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1') : item_video_src }
                            className="absolute top-0 left-0 right-0 bottom-0 w-full h-full"
                            allow="autoplay; fullscreen; picture-in-picture" allowFullScreen>
                        </iframe>
                    </div>
                )}

                {item_video_type === 'livid' && (
                    <div className="relative h-full w-full" style={{paddingTop: '56.25%'}}>
                        <iframe src={ source === 'modal' && item_video_src && !/autoplay=1/.test(item_video_src) ? (item_video_src + (item_video_src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1') : item_video_src }
                            className="absolute top-0 left-0 right-0 bottom-0 w-full h-full"
                            allow="autoplay; fullscreen; picture-in-picture" allowFullScreen>
                        </iframe>
                    </div>
                )}

                {(item_video_type !== 'youtube' && item_video_type !== 'vimeo' && item_video_type !== 'livid') && (
                    <video className="w-full h-full" controls {...(source === 'modal' ? {autoPlay: true} : {})}>
                        <source src={item_video_src}/>
                    </video>
                )}
            </div>
        );
    }
}
